import React, { useEffect, useState } from "react";
import { Button, Col, Form, Offcanvas, Row } from "react-bootstrap";
import type { DashboardWidget } from "../../../types/dashboard/dashboard.types";

type Props = {
  show: boolean;
  widget: DashboardWidget | null;
  onClose: () => void;
  onSave: (widgetId: string, content: string) => void;
  onLayoutChange: (
    widgetId: string,
    patch: Partial<Pick<DashboardWidget, "x" | "y" | "w" | "h">>
  ) => void;
};

const defaultForm = {
  numberOfItem: "0",
  title: "",
  icon: "car",
  color: "bg-primary",
  linkTo: "/vehicles",
  drivingValue: "0",
  parkingValue: "0",
  parkingEngineRunningValue: "0",
  lastTransmissionValue: "0",
  psn: "",
};

function parseConfig(content?: string) {
  try {
    const parsed = JSON.parse(content || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export default function WidgetSettingsDrawer({
  show,
  widget,
  onClose,
  onSave,
  onLayoutChange,
}: Props) {
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (!widget) return;
    const config: any = parseConfig(widget.content);
    setForm({
      numberOfItem: String(config.numberOfItem ?? defaultForm.numberOfItem),
      title: String(config.title ?? defaultForm.title),
      icon: String(config.icon ?? defaultForm.icon),
      color: String(config.color ?? defaultForm.color),
      linkTo: String(config.linkTo ?? defaultForm.linkTo),
      drivingValue: String(config.DrivingValue ?? defaultForm.drivingValue),
      parkingValue: String(config.ParkingValue ?? defaultForm.parkingValue),
      parkingEngineRunningValue: String(config.ParkingEngineRunningValue ?? defaultForm.parkingEngineRunningValue),
      lastTransmissionValue: String(config.LastTransmissionValue ?? defaultForm.lastTransmissionValue),
      psn: String(config.psn ?? defaultForm.psn),
    });
  }, [widget]);

  const updateField = (field: keyof typeof defaultForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildContent = () => {
    switch (widget?.type) {
      case "fleet_state":
        return {
          DrivingValue: Number(form.drivingValue || 0),
          ParkingValue: Number(form.parkingValue || 0),
          ParkingEngineRunningValue: Number(form.parkingEngineRunningValue || 0),
          LastTransmissionValue: Number(form.lastTransmissionValue || 0),
        };
      case "stats":
        return { psn: form.psn.trim() };
      case "alerts":
      case "fleet_co2":
        return parseConfig(widget.content);
      case "fleet_counter":
      default:
        return {
          numberOfItem: Number(form.numberOfItem || 0),
          title: form.title.trim() || "Counter",
          icon: form.icon.trim() || "car",
          color: form.color,
          linkTo: form.linkTo.trim() || "/vehicles",
        };
    }
  };

  const save = () => {
    if (!widget) return;
    onSave(widget.id, JSON.stringify(buildContent()));
    onClose();
  };

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" className="dash2-settings-drawer">
      <Offcanvas.Header closeButton>
        <div>
          <Offcanvas.Title>Modifier le widget</Offcanvas.Title>
          <div className="dash2-drawer-subtitle">{widget?.type}</div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body>
        {widget && (
          <>
            <div className="dash2-drawer-section">
              <div className="dash2-drawer-title">Disposition</div>
              <div className="dash2-width-presets">
                {[
                  { label: "1 col", w: 12 },
                  { label: "1/2", w: 6 },
                  { label: "1/3", w: 4 },
                  { label: "1/4", w: 3 },
                  { label: "70%", w: 8 },
                  { label: "30%", w: 4 },
                ].map((item) => (
                  <button
                    type="button"
                    key={item.label}
                    className={widget.w === item.w ? "active" : ""}
                    onClick={() => onLayoutChange(widget.id, { w: item.w })}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Largeur ({widget.w})</Form.Label>
                <Form.Range min={1} max={12} value={widget.w} onChange={(e) => onLayoutChange(widget.id, { w: Number(e.target.value) })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Hauteur ({widget.h})</Form.Label>
                <Form.Range min={2} max={16} value={widget.h} onChange={(e) => onLayoutChange(widget.id, { h: Number(e.target.value) })} />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Label>X</Form.Label>
                  <Form.Control type="number" value={widget.x} onChange={(e) => onLayoutChange(widget.id, { x: Number(e.target.value) })} />
                </Col>
                <Col>
                  <Form.Label>Y</Form.Label>
                  <Form.Control type="number" value={widget.y} onChange={(e) => onLayoutChange(widget.id, { y: Number(e.target.value) })} />
                </Col>
              </Row>
            </div>

            <div className="dash2-drawer-section">
              <div className="dash2-drawer-title">Contenu</div>

              {widget.type === "fleet_counter" && (
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Label>Titre</Form.Label>
                    <Form.Control value={form.title} onChange={(e) => updateField("title", e.target.value)} />
                  </Col>
                  <Col xs={12}>
                    <Form.Label>Valeur</Form.Label>
                    <Form.Control type="number" value={form.numberOfItem} onChange={(e) => updateField("numberOfItem", e.target.value)} />
                  </Col>
                  <Col xs={12}>
                    <Form.Label>Icone</Form.Label>
                    <Form.Control value={form.icon} onChange={(e) => updateField("icon", e.target.value)} />
                  </Col>
                  <Col xs={12}>
                    <Form.Label>Couleur</Form.Label>
                    <Form.Select value={form.color} onChange={(e) => updateField("color", e.target.value)}>
                      <option value="bg-primary">Bleu</option>
                      <option value="bg-info-light">Info</option>
                      <option value="bg-danger-light">Rouge</option>
                      <option value="bg-success-light">Vert</option>
                      <option value="bg-success-user">Utilisateur</option>
                    </Form.Select>
                  </Col>
                  <Col xs={12}>
                    <Form.Label>Lien</Form.Label>
                    <Form.Control value={form.linkTo} onChange={(e) => updateField("linkTo", e.target.value)} />
                  </Col>
                </Row>
              )}

              {widget.type === "fleet_state" && (
                <Row className="g-3">
                  <Col xs={6}>
                    <Form.Label>En route</Form.Label>
                    <Form.Control type="number" value={form.drivingValue} onChange={(e) => updateField("drivingValue", e.target.value)} />
                  </Col>
                  <Col xs={6}>
                    <Form.Label>Parking</Form.Label>
                    <Form.Control type="number" value={form.parkingValue} onChange={(e) => updateField("parkingValue", e.target.value)} />
                  </Col>
                  <Col xs={6}>
                    <Form.Label>Moteur marche</Form.Label>
                    <Form.Control type="number" value={form.parkingEngineRunningValue} onChange={(e) => updateField("parkingEngineRunningValue", e.target.value)} />
                  </Col>
                  <Col xs={6}>
                    <Form.Label>Sans transmission</Form.Label>
                    <Form.Control type="number" value={form.lastTransmissionValue} onChange={(e) => updateField("lastTransmissionValue", e.target.value)} />
                  </Col>
                </Row>
              )}

              {widget.type === "stats" && (
                <Form.Group>
                  <Form.Label>PSN dispositif</Form.Label>
                  <Form.Control value={form.psn} onChange={(e) => updateField("psn", e.target.value)} />
                </Form.Group>
              )}

              {(widget.type === "alerts" || widget.type === "fleet_co2") && (
                <div className="dash2-drawer-muted">Ce widget utilise les donnees existantes. Les options avancees seront ajoutees ici.</div>
              )}
            </div>

            <div className="dash2-drawer-actions">
              <Button variant="secondary" onClick={onClose}>Fermer</Button>
              <Button variant="primary" onClick={save}>Appliquer</Button>
            </div>
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}
